import { useEffect, useState } from 'react'
import { Card, CardContent } from '@klear/ui/components/card'
import { ChevronDown, ChevronRight, FileText } from 'lucide-react'
import { Button } from '@klear/ui/components/button'
import { useMonthlyStatements } from '@/hooks/use-monthly-statements'
import LoadingSpinner from '@klear/ui/components/spinner'
import { WireTransferDetailsDocument } from '@/components/pdf/wire-transfer-details'
import { pdf } from '@react-pdf/renderer'
import { PdfModalWithDownload } from '@/components/pdf/pdf-modal'
import { traderToOrganization } from '@klear/trader/organization-bridge'
import { useTrader } from '@klear/ui/hooks/use-trader'
import { useKlearFinancialAccount, klearAccountOptionLabel } from '@klear/ui/hooks/use-klear-financial-account'
import { useParams } from 'react-router'
import { KlearDate } from '@klear/date'
import MonthlyAccountStatementsDocument from '@/components/pdf/monthly-account-statements'
import { useKlearAccountTransactions } from '@/hooks/use-klear-account-transactions'

type DocumentType = 'system-documents' | 'monthly-statements'

interface DocumentCardProps {
  title: string
  description: string
  available: boolean
  pdfDocument: React.ReactElement
  pdfFileName: string
}

const DocumentCard = ({ title, description, available, pdfDocument, pdfFileName }: DocumentCardProps) => {
  const [fileSize, setFileSize] = useState<string>('')

  useEffect(() => {
    ;(async () => {
      const docInstance = pdf()
      docInstance.updateContainer(pdfDocument)
      const blob = await docInstance.toBlob()
      const fileSizeInBytes = blob.size
      const fileSizeInKB = (fileSizeInBytes / 1024).toFixed(1)
      const fileSizeInMB = (fileSizeInBytes / (1024 * 1024)).toFixed(2)

      if (fileSizeInBytes >= 1024 * 1024) {
        setFileSize(`${fileSizeInMB} MB`)
      } else {
        setFileSize(`${fileSizeInKB} KB`)
      }
    })()
  }, [pdfDocument])

  return (
    <>
      <Card>
        <CardContent className="flex items-center gap-3 p-4 border rounded-lg">
          <div className="rounded-lg p-2 bg-blue-100 dark:bg-blue-950/40">
            <FileText className="w-5 h-5 text-blue-700 dark:text-blue-300" />
          </div>
          <div className="flex flex-col gap-0 flex-1">
            <p className="font-medium">{title}</p>
            <p className="text-sm text-muted-foreground">
              {description} <span className="mx-1">•</span> {fileSize}
            </p>
          </div>
          <p
            className={`font-semibold text-sm rounded-lg px-2 py-1 ${available ? 'text-green-700 bg-green-100 dark:text-green-300 dark:bg-green-950/40' : 'text-blue-700 bg-blue-100 dark:text-blue-300 dark:bg-blue-950/40'}`}
          >
            {available ? 'Available' : 'Processing'}
          </p>
          {available && (
            <div className="flex items-center gap-2">
              <PdfModalWithDownload pdfDocument={pdfDocument} title={title} filename={pdfFileName} />
            </div>
          )}
        </CardContent>
      </Card>
    </>
  )
}

interface SignedDocumentCardProps {
  title: string
  description: string
  pdfUrl: string | null
  pdfFileName: string
}

const SignedDocumentCard = ({ title, description, pdfUrl, pdfFileName }: SignedDocumentCardProps) => {
  const available = !!pdfUrl
  return (
    <Card>
      <CardContent className="flex items-center gap-3 p-4 border rounded-lg">
        <div className="rounded-lg p-2 bg-blue-100 dark:bg-blue-950">
          <FileText className="w-5 h-5 text-blue-700 dark:text-blue-300" />
        </div>
        <div className="flex flex-col gap-0 flex-1">
          <p className="font-medium">{title}</p>
          <p className="text-sm text-muted-foreground">
            {description}
            {available && (
              <>
                <span className="mx-1">•</span> PDF
              </>
            )}
          </p>
        </div>
        <p
          className={`font-semibold text-sm rounded-lg px-2 py-1 ${available ? 'text-green-700 bg-green-100 dark:text-green-300 dark:bg-green-950' : 'text-blue-700 bg-blue-100 dark:text-blue-300 dark:bg-blue-950'}`}
        >
          {available ? 'Available' : 'Not available'}
        </p>
        {available && pdfUrl && (
          <div className="flex items-center gap-2">
            <PdfModalWithDownload pdfUrl={pdfUrl} title={title} filename={pdfFileName} />
          </div>
        )}
      </CardContent>
    </Card>
  )
}

const Documents = () => {
  const { klearFinancialAccount, accounts, signedKlearDocUrlsByAccountId } = useKlearFinancialAccount()
  const [open, setOpen] = useState(false)
  const [documentTypeSelected, setDocumentTypeSelected] = useState<DocumentType>('system-documents')

  const handleClick = () => {
    setOpen((prev) => !prev)
  }
  const formatNextMonthlyStatementDate = () => {
    const today = KlearDate.today()
    const tenthThisMonth = today.startOf('month').add(9, 'day')

    const nextStatementDate = today.isBefore(tenthThisMonth)
      ? tenthThisMonth
      : today.add(1, 'month').startOf('month').add(9, 'day')
    return KlearDate.format(nextStatementDate, 'MMMM D')
  }
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex gap-3 items-center">
          <div className="rounded-xl p-2 bg-muted">
            <FileText className="w-6 h-6 text-muted-foreground z-10" />
          </div>
          <div className="flex flex-col gap-0">
            <p className="font-bold">Documents & statements</p>
            <p className="text-muted-foreground text-sm font-medium">Account documents and monthly statements</p>
          </div>
          <button onClick={handleClick} className="ml-auto hover:bg-accent rounded-full p-1">
            {open ? (
              <ChevronDown className="w-5 h-5 text-muted-foreground ml-auto" />
            ) : (
              <ChevronRight className="w-5 h-5 text-muted-foreground ml-auto" />
            )}
          </button>
        </div>
        {open && (
          <div className="mt-4">
            <div className="flex gap-2 bg-muted rounded-lg p-1">
              <button
                className={`w-1/2 p-1 rounded-lg transition-all duration-200 ${documentTypeSelected === 'system-documents' ? 'font-medium bg-background border border-border shadow-lg' : 'hover:bg-accent'}`}
                onClick={() => setDocumentTypeSelected('system-documents')}
              >
                System documents
              </button>
              <button
                className={`w-1/2 p-1 rounded-lg transition-all duration-200 ${documentTypeSelected === 'monthly-statements' ? 'font-medium bg-background border border-border shadow-lg' : 'hover:bg-accent'}`}
                onClick={() => setDocumentTypeSelected('monthly-statements')}
              >
                Monthly statements
              </button>
            </div>
            {documentTypeSelected === 'system-documents' && (
              <div className="mt-6">
                <div className="flex justify-between items-center font-semibold">
                  <p className="text-sm text-muted-foreground">Bank verification letters and wire transfer detials</p>
                  <Button variant="outline">Request Document</Button>
                </div>
                <div className="flex flex-col gap-2 mt-4">
                  {klearFinancialAccount && (
                    <DocumentCard
                      title="Wire transfer details"
                      pdfFileName="wire-transfer-details.pdf"
                      description="Wire Info"
                      available={true}
                      pdfDocument={<WireTransferDetailsDocument klearFinancialAccount={klearFinancialAccount} />}
                    />
                  )}
                  {accounts.map((account) => {
                    const url = account.id != null ? signedKlearDocUrlsByAccountId.get(account.id) : undefined
                    if (!url) return null
                    return (
                      <SignedDocumentCard
                        key={account.id}
                        title={`Klear account agreement — ${klearAccountOptionLabel(account)}`}
                        description="Your signed Klear account agreement"
                        pdfUrl={url}
                        pdfFileName={`klear-account-agreement-${account.id}.pdf`}
                      />
                    )
                  })}
                </div>
              </div>
            )}
            {documentTypeSelected === 'monthly-statements' && (
              <div className="mt-6">
                <div className="flex justify-between items-center font-semibold">
                  <p className="text-sm text-muted-foreground">Generated monthly on the 10th of each month</p>
                  <p className="text-sm text-muted-foreground">Next: {formatNextMonthlyStatementDate()}</p>
                </div>
                <div className="flex flex-col gap-2 mt-4">
                  <MonthlyAccountStatements />
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function MonthlyAccountStatements() {
  const { traderId } = useParams()
  const { trader, isLoading: isLoadingTrader } = useTrader(traderId)
  const { klearFinancialAccount, isLoading: isLoadingKlearFinancialAccount } = useKlearFinancialAccount()
  const { transactions, isLoading: isLoadingTransactions } = useKlearAccountTransactions(0)
  const monthlyStatements = useMonthlyStatements(klearFinancialAccount, transactions)
  if (isLoadingTransactions || isLoadingKlearFinancialAccount || isLoadingTrader) {
    return <LoadingSpinner className="h-20 w-full min-h-20" />
  }

  if (!klearFinancialAccount || !monthlyStatements || !trader) {
    return (
      <div>
        <p>No financial transactions found</p>
      </div>
    )
  }

  return monthlyStatements.map((statement) => (
    <DocumentCard
      key={statement.monthKey}
      title={`${statement.monthName} Statement`}
      pdfFileName={`${statement.monthKey}-statement.pdf`}
      description="Monthly Statement"
      available={true}
      pdfDocument={
        <MonthlyAccountStatementsDocument
          monthlyStatement={statement}
          klearFinancialAccount={klearFinancialAccount}
          organization={traderToOrganization(trader)}
        />
      }
    />
  ))
}

export function KlearFinancialAccountDetails() {
  const [open, setOpen] = useState(false)
  const { klearFinancialAccount } = useKlearFinancialAccount()

  const handleClick = () => {
    setOpen((prev) => !prev)
  }
  if (!klearFinancialAccount) {
    return null
  }
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex gap-3 items-center">
          <div className="rounded-xl p-2 bg-muted">
            <FileText className="w-6 h-6 text-muted-foreground z-10" />
          </div>
          <div className="flex flex-col gap-0">
            <p className="font-bold">Account Details</p>
          </div>
          <button onClick={handleClick} className="ml-auto hover:bg-accent rounded-full p-1">
            {open ? (
              <ChevronDown className="w-5 h-5 text-muted-foreground ml-auto" />
            ) : (
              <ChevronRight className="w-5 h-5 text-muted-foreground ml-auto" />
            )}
          </button>
        </div>
        {open && (
          <div className="flex gap-4 mt-4 items-center">
            <div className="p-6">
              <div className="grid gap-6">
                <div>
                  <h3 className="font-medium mb-2 flex items-center gap-2">
                    Account Details{' '}
                    {klearFinancialAccount.status && (
                      <span className="text-sm font-normal text-muted-foreground capitalize">
                        ({klearFinancialAccount.status})
                      </span>
                    )}
                  </h3>

                  <dl className="grid grid-cols-2 gap-2 text-sm">
                    <dt className="text-muted-foreground">Bank Name</dt>
                    <dd>{klearFinancialAccount.bankName}</dd>
                    <dt className="text-muted-foreground">Account Holder</dt>
                    <dd>{klearFinancialAccount.accountHolderName}</dd>
                    <dt className="text-muted-foreground">Account Number</dt>
                    <dd>{klearFinancialAccount.accountNumber}</dd>
                    <dt className="text-muted-foreground">Routing Number</dt>
                    <dd>{klearFinancialAccount.routingNumber}</dd>
                  </dl>
                </div>

                <div>
                  <h3 className="font-medium mb-2">Balance</h3>
                  {klearFinancialAccount.account == null ? (
                    <p className="text-sm text-muted-foreground">
                      Balances are unavailable until this account refreshes.
                    </p>
                  ) : (
                    <dl className="grid grid-cols-2 gap-2 text-sm">
                      <dt className="text-muted-foreground">Available</dt>
                      <dd>{klearFinancialAccount.balanceCash.toString()}</dd>
                      <dt className="text-muted-foreground">Pending Inbound</dt>
                      <dd>{klearFinancialAccount.balanceInboundPending.toString()}</dd>
                      <dt className="text-muted-foreground">Pending Outbound</dt>
                      <dd>{klearFinancialAccount.balanceOutboundPending.toString()}</dd>
                    </dl>
                  )}
                </div>

                <div>
                  <h3 className="font-medium mb-2">Features</h3>
                  <div className="flex flex-wrap gap-2">
                    {(klearFinancialAccount?.activeFeatures ?? []).map((feature: string, index: number) => (
                      <div key={index} className="px-2.5 py-0.5 rounded-full text-xs bg-secondary">
                        {feature.toUpperCase()}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default Documents
